import ChevronLeftSvg from "@/src/assets/icons/icon-chevron-left-black.svg";
import { Button } from "@/src/components/atom/Button";
import Icon from "@/src/components/atom/Icon";
import Loader from "@/src/components/atom/Loader";
import BottomFixedContainer from "@/src/components/molecule/BottomFixedContainer";
import FormItem from "@/src/components/molecule/FormItem";
import SectionCard from "@/src/components/molecule/SectionCard";
import ImporterMainLayout from "@/src/components/template/Layout/importer/ImporterMainLayout";
import useAlert from "@/src/hooks/useAlert";
import useContentLoading from "@/src/hooks/useContentLoading";
import IMPORTER_PRIVATE_PATH from "@/src/routes/importer/path";
import { useGetCommonCodeViaCodeNameQuery } from "@/src/store/apis/common";
import { useCreateInquiryMutation } from "@/src/store/apis/inquiry";
import { useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { styled } from "styled-components";

type OptionType = {
  value: string;
  label: string;
};

const INQUIRY_SORT = [
  "INQUIRY_PAYMENT_SUBSCRIPTION",
  "INQUIRY_ACCOUNT_PERMISSIONS",
  "INQUIRY_DASHBOARD",
  "INQUIRY_CONTRACT",
  "INQUIRY_BOOKING",
  "INQUIRY_TASK_WEB",
  "INQUIRY_TASK_APP",
  "INQUIRY_SHIPMENT",
  "INQUIRY_ETC",
];

const ImporterInquiryAddPage = () => {
  const { t, i18n } = useTranslation();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const alert = useAlert();
  const { handleContentLoadingOff, handleContentLoadingOn } =
    useContentLoading();
  const { control, handleSubmit, setFocus } = useForm({
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      category: undefined,
      title: "",
      content: "",
    },
  });
  const navigate = useNavigate();
  const {
    currentData: inquiryCategory,
    isFetching: isInquiryCategoryFetching,
  } = useGetCommonCodeViaCodeNameQuery(
    {
      codeName: "INQUIRY_CATEGORY",
    },
    { refetchOnMountOrArgChange: true },
  );
  const [createInquiry] = useCreateInquiryMutation();

  const sortedInquiryCategory = useMemo(
    () =>
      inquiryCategory?.slice().sort((prev, next) => {
        const prevIndex = INQUIRY_SORT.indexOf(prev.codeItemName);
        const nextIndex = INQUIRY_SORT.indexOf(next.codeItemName);

        if (prevIndex === -1) {
          return 1;
        }

        if (nextIndex === -1) {
          return -1;
        }

        return prevIndex - nextIndex;
      }) ?? [],
    [inquiryCategory],
  );

  const measurementListToOptionList = inquiryCategory
    ? sortedInquiryCategory.reduce<OptionType[]>((acc, val) => {
        const resource = {
          label:
            i18n.language === "ko" ? val.codeItemNameKo : val.codeItemNameEn,
          value: val.codeItemName,
        };
        return [...acc, { ...resource }];
      }, [])
    : [];

  const handleCreateInquiry = async ({
    category,
    content,
    title,
  }: {
    category: undefined;
    title: string;
    content: string;
  }) => {
    try {
      handleContentLoadingOn();
      const { id } = await createInquiry({
        category: category,
        title: title,
        body: content,
      }).unwrap();

      navigate(`${IMPORTER_PRIVATE_PATH.INQUIRY}/${id}`);
      alert.showAlert({
        type: "success",
        message: t("alert:saveSuccess"),
      });
    } catch (e: any) {
      const message = Array.isArray(e.data.message)
        ? e.data.message[0]
        : e.data.message;
      alert.showAlert({ type: "error", message });
    } finally {
      handleContentLoadingOff();
    }
  };

  useEffect(() => {
    setFocus("category");
  }, [setFocus]);

  return (
    <>
      <ImporterMainLayout
        breadcrumb={[t("sideNav:inquiry"), t("sideNav:inquiryRegistration")]}
        pageTitle={t("sideNav:inquiryRegistration")}
      >
        <Article>
          <SectionCard cardTitle={t("sideNav:inquiry")}>
            <StyledForm onSubmit={handleSubmit(handleCreateInquiry)}>
              <FormItem
                label={t("inquiry:label.category")}
                rules={{
                  required: true,
                }}
                type="singleSelect"
                control={control}
                name="category"
                inputProps={{
                  suffixIcon: isInquiryCategoryFetching ? (
                    <Loader />
                  ) : undefined,
                  disabled: isInquiryCategoryFetching,
                  placeholder: t("inquiry:placeholder.selectCategory"),
                  filterOption: (input, option) =>
                    ((option?.label as string) ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase()),
                }}
                options={measurementListToOptionList}
                errorsMessage={{
                  required: t("error:required"),
                }}
              />
              <FormItem
                label={t("inquiry:label.title")}
                type={"text"}
                name={"title"}
                control={control}
                inputProps={{
                  placeholder: t("inquiry:placeholder.enterTitle"),
                }}
                rules={{
                  required: true,
                }}
                errorsMessage={{
                  required: t("error:required"),
                }}
              />
              <FormItem
                label={t("inquiry:label.inquiryDetail")}
                type={"textarea"}
                name={"content"}
                control={control}
                inputProps={{
                  placeholder: t("inquiry:placeholder.enterContents"),
                  maxLength: 2000,
                  style: {
                    height: "500px",
                  },
                }}
                rules={{
                  required: true,
                }}
                errorsMessage={{
                  required: t("error:required"),
                }}
              />
              <Hidden ref={buttonRef} type="submit" />
            </StyledForm>
          </SectionCard>

          <BottomFixedContainer>
            <FooterButtonSection>
              <Button
                buttonGrade="tertiary"
                buttonColor="black"
                style={{ display: "flex", alignItems: "center", gap: "4px" }}
                onClick={() => navigate(-1)}
              >
                <Icon iconSrc={ChevronLeftSvg} iconSize={16} />
                {t("common:backToPrevious")}
              </Button>
              <Button
                buttonGrade="primary"
                style={{ width: 158, textAlign: "center" }}
                type="submit"
                onClick={() => buttonRef.current?.click()}
              >
                {t("common:save")}
              </Button>
            </FooterButtonSection>
          </BottomFixedContainer>
        </Article>
      </ImporterMainLayout>
    </>
  );
};

export default ImporterInquiryAddPage;

const Article = styled.article`
  margin-bottom: 72px;
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 24px;
`;

const FooterButtonSection = styled.section`
  display: flex;
  justify-content: space-between;
`;

const Hidden = styled.button`
  position: absolute;
  width: 0;
  height: 0;
  padding: 0;
  overflow: hidden;
  border: 0;
`;
